import {
  CalendarDays,
  CheckCircle2,
  Download,
  FileText,
  LockKeyhole,
  ReceiptText,
  ShieldCheck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { PaymentQrPanel } from "@/components/invoice/PaymentQrPanel";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { FieldLabel, Input } from "@/components/ui/Field";
import { ErrorPanel, LoadingPanel } from "@/components/ui/Loading";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { billingTypeLabel, formatCurrency, formatItemPricingDetail } from "@/domain/billing";
import { formatDate, formatDateRange } from "@/domain/dates";
import type { InvoiceRecord, PublicInvoicePayload } from "@/domain/types";
import { displayStatus } from "@/features/invoices/status";
import { downloadInvoicePdf } from "@/features/pdf/invoice-pdf";
import { downloadReceiptPdf } from "@/features/pdf/receipt-pdf";
import { ApiError, apiRequest } from "@/lib/api";

function detailForItem(
  invoice: InvoiceRecord,
  index: number,
): string {
  const item = invoice.items[index]!;
  return formatItemPricingDetail(
    item,
    invoice.currency,
  );
}

function isPinChallenge(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.code === "PIN_REQUIRED" || error.code === "PIN_INVALID");
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read payment QR image."));
    reader.readAsDataURL(blob);
  });
}

async function hydratePublicPaymentQrAssets(
  invoice: InvoiceRecord,
  invoicePin: string,
): Promise<InvoiceRecord> {
  const entries = await Promise.all(
    (["gcash", "maribank"] as const).map(async (method) => {
      const asset = invoice.paymentQrAssets[method];
      if (!asset.imageUrl || !asset.hasImage) return [method, asset] as const;
      const headers = new Headers();
      if (invoicePin) headers.set("X-Invoice-PIN", invoicePin);
      const response = await fetch(asset.imageUrl, { headers, cache: "no-store" });
      if (!response.ok) return [method, { ...asset, imageUrl: null, hasImage: false }] as const;
      const imageUrl = await blobToDataUrl(await response.blob());
      return [method, { ...asset, imageUrl }] as const;
    }),
  );

  return {
    ...invoice,
    paymentQrAssets: Object.fromEntries(entries) as InvoiceRecord["paymentQrAssets"],
  };
}

export function PublicInvoicePage() {
  const { token = "" } = useParams();
  const storageKey = useMemo(() => `ledgerly:invoice-pin:${token}`, [token]);
  const [invoice, setInvoice] = useState<InvoiceRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [needsPin, setNeedsPin] = useState(false);
  const [pin, setPin] = useState(() => sessionStorage.getItem(storageKey) ?? "");
  const [pinMessage, setPinMessage] = useState("");

  const loadInvoice = useCallback(async (
    candidatePin: string,
    options: { background?: boolean; countView?: boolean } = {},
  ) => {
    const background = options.background ?? false;
    const countView = options.countView ?? true;

    if (!background) {
      setLoading(true);
      setError(null);
      setPinMessage("");
    }

    try {
      const refreshQuery = countView ? "" : "?refresh=1";
      const payload = await apiRequest<PublicInvoicePayload>(
        `/api/public/invoices/${token}${refreshQuery}`,
        {
          invoicePin: candidatePin || undefined,
          cache: "no-store",
        },
      );

      if (background) {
        setInvoice((current) =>
          current?.id === payload.invoice.id
            ? { ...payload.invoice, paymentQrAssets: current.paymentQrAssets }
            : payload.invoice,
        );
      } else {
        setInvoice(await hydratePublicPaymentQrAssets(payload.invoice, candidatePin));
      }
      setNeedsPin(false);
      setError(null);

      if (candidatePin) {
        sessionStorage.setItem(storageKey, candidatePin);
      }
    } catch (requestError) {
      if (isPinChallenge(requestError)) {
        setNeedsPin(true);
        setInvoice(null);
        setPinMessage(
          requestError.code === "PIN_INVALID"
            ? "That PIN is not correct. Please try again."
            : "Enter the PIN provided with this invoice link.",
        );

        if (requestError.code === "PIN_INVALID") {
          sessionStorage.removeItem(storageKey);
        }
      } else if (
        !background ||
        (requestError instanceof ApiError &&
          (requestError.status === 404 || requestError.status === 410))
      ) {
        setInvoice(null);
        setError(
          requestError instanceof Error
            ? requestError
            : new Error("This invoice could not be loaded."),
        );
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, [storageKey, token]);

  const refreshInvoice = useCallback(() => {
    void loadInvoice(
      sessionStorage.getItem(storageKey) ?? "",
      { background: true, countView: false },
    );
  }, [loadInvoice, storageKey]);

  useEffect(() => {
    void loadInvoice(sessionStorage.getItem(storageKey) ?? "");
  }, [loadInvoice, storageKey]);

  useEffect(() => {
    if (!invoice || needsPin) {
      return undefined;
    }

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        refreshInvoice();
      }
    };

    const intervalId = window.setInterval(refreshWhenVisible, 30_000);

    window.addEventListener("focus", refreshInvoice);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshInvoice);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [invoice?.id, needsPin, refreshInvoice]);

  if (loading) return <LoadingPanel label="Opening secure invoice…" />;

  if (needsPin && !invoice) {
    return (
      <div className="mx-auto max-w-md py-8 sm:py-14">
        <Card className="overflow-hidden">
          <div className="bg-ink px-6 py-7 text-white">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
              <LockKeyhole className="h-6 w-6 text-brand-300" />
            </div>
            <h1 className="mt-5 text-2xl font-black">Protected invoice</h1>
            <p className="mt-2 text-sm leading-6 text-white/70">This client link has an extra PIN for privacy.</p>
          </div>
          <form
            className="p-6"
            onSubmit={(event) => {
              event.preventDefault();
              void loadInvoice(pin);
            }}
          >
            <FieldLabel htmlFor="invoice-pin" required>Invoice PIN</FieldLabel>
            <Input
              id="invoice-pin"
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 12))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="4–12 digits"
              aria-describedby="invoice-pin-help"
              autoFocus
            />
            <p id="invoice-pin-help" className={`mt-2 text-xs ${pinMessage.includes("not correct") ? "text-danger-700" : "text-ink-muted"}`}>{pinMessage}</p>
            <Button className="mt-5 w-full" variant="brand" type="submit" disabled={pin.length < 4}>
              <ShieldCheck className="h-4 w-4" />
              View invoice
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  if (error || !invoice) {
    return <ErrorPanel error={error ?? new Error("Invoice not found.")} onRetry={() => void loadInvoice(sessionStorage.getItem(storageKey) ?? "")} />;
  }

  const status = displayStatus(invoice);
  const paid = invoice.status === "paid";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-700">Secure client invoice</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-ink sm:text-4xl">{invoice.invoiceNumber}</h1>
          <p className="mt-2 text-sm text-ink-muted">Issued {formatDate(invoice.issueDate)} · {invoice.company.name}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => downloadInvoicePdf(invoice)}>
            <Download className="h-4 w-4" />
            Invoice PDF
          </Button>
          {paid ? (
            <Button variant="brand" onClick={() => downloadReceiptPdf(invoice)}>
              <ReceiptText className="h-4 w-4" />
              Receipt PDF
            </Button>
          ) : null}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="relative overflow-hidden bg-ink px-5 py-7 text-white sm:px-8 sm:py-9">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-brand-400/10" />
          <div className="absolute -bottom-20 right-16 h-40 w-40 rounded-full bg-gold-300/10" />
          <div className="relative flex flex-col gap-7 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-300">Billed to</p>
              <h2 className="mt-2 text-2xl font-black">{invoice.client.name}</h2>
              {invoice.client.company ? <p className="mt-1 text-sm text-white/70">{invoice.client.company}</p> : null}
              {invoice.client.email ? <p className="mt-3 text-sm text-white/65">{invoice.client.email}</p> : null}
            </div>
            <div className="flex flex-col items-start gap-3 lg:items-end">
              <StatusBadge status={status} className={paid ? "bg-brand-300 text-brand-900" : undefined} />
              <div className="lg:text-right">
                <p className="text-xs font-bold uppercase tracking-wider text-white/55">Balance due</p>
                <p className={`mt-1 text-3xl font-black ${paid ? "text-brand-300" : "text-gold-300"}`}>
                  {formatCurrency(invoice.totals.balanceCents, invoice.currency)}
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-wider text-white/55">
                  {invoice.currency}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Invoice date", formatDate(invoice.issueDate)],
            ["Service period", formatDateRange(invoice.serviceStartDate, invoice.serviceEndDate)],
            ["Payment terms", invoice.dueLabel],
            ["Due date", invoice.dueDate ? formatDate(invoice.dueDate) : "Per agreed terms"],
          ].map(([label, value]) => (
            <div key={label} className="bg-white px-5 py-5 sm:px-6">
              <p className="text-xs font-bold uppercase tracking-wider text-ink-muted">{label}</p>
              <p className="mt-2 text-sm font-bold text-ink">{value}</p>
            </div>
          ))}
        </div>
      </Card>

      {paid ? (
        <Card className="border-brand-200 bg-brand-50 p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-700"><CheckCircle2 className="h-6 w-6" /></div>
            <div>
              <h2 className="font-black text-brand-800">Payment received</h2>
              <p className="mt-1 text-sm leading-6 text-brand-800/75">
                Paid {formatDate(invoice.payments[0]?.paidAt ?? invoice.paidAt)} via {invoice.payments[0]?.method ?? "recorded payment"}.
                {invoice.payments[0]?.reference ? ` Reference: ${invoice.payments[0].reference}.` : ""}
              </p>
            </div>
          </div>
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <div className="flex items-center gap-3 border-b border-border px-5 py-5 sm:px-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-muted text-ink"><FileText className="h-5 w-5" /></div>
          <div><h2 className="font-black text-ink">Services provided</h2><p className="text-xs text-ink-muted">Itemized work covered by this invoice</p></div>
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[720px] border-collapse text-left">
            <thead className="bg-surface-muted text-xs font-bold uppercase tracking-wider text-ink-muted">
              <tr><th className="px-6 py-4">Service / deliverable</th><th className="px-4 py-4">Billing</th><th className="px-4 py-4">Detail</th><th className="px-6 py-4 text-right">Amount</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoice.items.map((item, index) => (
                <tr key={item.id}>
                  <td className="px-6 py-5 font-semibold text-ink">{item.description}</td>
                  <td className="px-4 py-5 text-sm text-ink-muted">{billingTypeLabel(item.billingType)}</td>
                  <td className="px-4 py-5 text-sm text-ink-muted">{detailForItem(invoice, index)}</td>
                  <td className="px-6 py-5 text-right font-bold text-ink">{formatCurrency(item.amountCents, invoice.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="divide-y divide-border md:hidden">
          {invoice.items.map((item, index) => (
            <div key={item.id} className="p-5">
              <div className="flex items-start justify-between gap-4"><p className="font-bold text-ink">{item.description}</p><p className="shrink-0 font-black text-ink">{formatCurrency(item.amountCents, invoice.currency)}</p></div>
              <p className="mt-2 text-sm text-ink-muted">{billingTypeLabel(item.billingType)} · {detailForItem(invoice, index)}</p>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Card className="p-5 sm:p-6">
          <div className="flex items-center gap-3"><CalendarDays className="h-5 w-5 text-brand-700" /><h2 className="font-black text-ink">Payment information</h2></div>
          <div className="mt-4 space-y-3 text-sm leading-6 text-ink-muted">
            {invoice.paymentTerms ? <p>{invoice.paymentTerms}</p> : null}
            {invoice.paymentInstructions ? <p className="whitespace-pre-wrap font-medium text-ink">{invoice.paymentInstructions}</p> : null}
            {invoice.publicNotes ? <p className="whitespace-pre-wrap">{invoice.publicNotes}</p> : null}
            {!invoice.paymentTerms && !invoice.paymentInstructions && !invoice.publicNotes ? <p>Please contact {invoice.company.name} for payment details.</p> : null}
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between gap-4 text-ink-muted"><span>Subtotal</span><span className="font-semibold text-ink">{formatCurrency(invoice.totals.subtotalCents, invoice.currency)}</span></div>
            <div className="flex justify-between gap-4 text-ink-muted"><span>Tax</span><span className="font-semibold text-ink">{formatCurrency(invoice.totals.taxCents, invoice.currency)}</span></div>
            <div className="border-t border-border pt-3"><div className="flex items-end justify-between gap-4"><span className="font-black text-ink">Total</span><span className="text-2xl font-black text-ink">{formatCurrency(invoice.totals.totalCents, invoice.currency)}</span></div></div>
            {paid ? <div className="flex justify-between gap-4 rounded-xl bg-brand-50 px-3 py-2.5 text-brand-800"><span className="font-bold">Balance</span><span className="font-black">{formatCurrency(0, invoice.currency)}</span></div> : null}
          </div>
        </Card>
      </div>

      {!paid ? <PaymentQrPanel invoice={invoice} /> : null}

      <p className="flex items-center justify-center gap-2 pb-4 text-center text-xs text-ink-muted"><ShieldCheck className="h-4 w-4" />Read-only secure invoice link. Other invoices and admin records are not accessible from this page.</p>
    </div>
  );
}
